import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { BellRing, Loader2, Send } from "lucide-react";

const PRESETS = {
  listini: {
    title: "Aggiornamento listini prezzi",
    message: "Abbiamo aggiornato i listini prezzi nel Portale B2B. Ti invitiamo a consultare il catalogo per le condizioni aggiornate.",
  },
  sconti: {
    title: "Aggiornamento condizioni di vendita",
    message: "Abbiamo aggiornato le condizioni di vendita (sconti a scaglioni) nel Portale B2B. Consulta la sezione «Condizioni di vendita» per i dettagli.",
  },
  spedizioni: {
    title: "Aggiornamento spese di spedizione",
    message: "Abbiamo aggiornato le regole e le spese di spedizione nel Portale B2B. Consulta la sezione «Spedizioni» per i dettagli.",
  },
  generico: {
    title: "Comunicazione da Brema Bike",
    message: "",
  },
};

/**
 * Pulsante admin: avvisa i Rivenditori Ufficiali di una modifica (listini / sconti / spedizioni).
 * L'invio avviene SOLO dopo la conferma esplicita dell'admin nel dialog (in-app + email opzionale).
 */
export function NotifyResellersButton({ category = "generico", className = "", testId = "notify-resellers-btn" }) {
  const preset = PRESETS[category] || PRESETS.generico;
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(preset.title);
  const [message, setMessage] = useState(preset.message);
  const [sendEmail, setSendEmail] = useState(true);
  const [sending, setSending] = useState(false);

  const openDialog = () => {
    setTitle(preset.title);
    setMessage(preset.message);
    setSendEmail(true);
    setOpen(true);
  };

  const submit = async () => {
    if (!title.trim()) { toast.error("Inserisci un titolo per l'avviso"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/reseller-notifications", {
        title: title.trim(), message: message.trim(), category, send_email: sendEmail,
      });
      if (sendEmail) {
        toast.success(`Avviso pubblicato · email inviata a ${data.email_sent || 0} rivenditori ufficiali`);
      } else {
        toast.success("Avviso pubblicato nel Portale dei rivenditori");
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invio avviso fallito");
    } finally { setSending(false); }
  };

  return (
    <>
      <Button type="button" variant="secondary" size="sm" onClick={openDialog} data-testid={testId}
        className={`rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover ${className}`}>
        <BellRing className="mr-1.5 h-3.5 w-3.5" /> Avvisa i rivenditori
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg border-border bg-brema-card" data-testid="notify-resellers-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Avvisa i Rivenditori Ufficiali</DialogTitle>
            <DialogDescription>
              L'avviso comparirà nel Portale B2B dei Rivenditori Ufficiali. Puoi anche inviarlo via email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Titolo</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)}
                data-testid="notify-title-input" className="bg-brema-bg" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Messaggio (facoltativo)</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
                data-testid="notify-message-input" className="bg-brema-bg" />
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground" data-testid="notify-send-email-row">
              <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(!!v)}
                data-testid="notify-send-email" className="border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
              Invia anche via email a tutti i Rivenditori Ufficiali
            </label>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)} data-testid="notify-cancel-btn"
              className="rounded-full border border-border bg-transparent text-sm text-foreground hover:bg-brema-hover">
              Annulla
            </Button>
            <Button onClick={submit} disabled={sending} data-testid="notify-confirm-btn"
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio…</> : <><Send className="mr-2 h-4 w-4" /> Pubblica avviso</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
